import type { BillingUpgradeDowngradeModalProps } from "../AppModalTypes";

import * as React from "react";

import useInitializeSubscriptionUpdateMutation from "@editor/hooks/trpc/useInitializeSubscriptionUpdateMutation";
import { useModal } from "@editor/hooks/useModal";
import { trpc } from "@editor/utils/trpc";

import { successToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import { skipToken } from "@tanstack/react-query";
import { ArrowRight, Info } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { capitalizeFirstLetter } from "replo-utils/lib/string";
import { getBillingPlanInfoByTier } from "schemas/billing";

import { PlanInfoDetails } from "./BillingModal";

export const BillingUpgradeDowngradeModal: React.FC<
  BillingUpgradeDowngradeModalProps
> = ({
  workspaceId,
  currentTier,
  newTier,
  type,
  source,
  newBillingInterval,
}) => {
  const modal = useModal();
  const { initializeSubscriptionUpdate, isLoading } =
    useInitializeSubscriptionUpdateMutation();

  const isUpgradePlanYearly = newBillingInterval === "yearly";

  const currentTierName = capitalizeFirstLetter(currentTier);
  const newTierName = capitalizeFirstLetter(newTier);

  const currentPlanInfo = getBillingPlanInfoByTier(currentTier);
  const newPlanInfo = getBillingPlanInfoByTier(newTier);

  const copy = exhaustiveSwitch({ type })({
    upgrade: {
      header: `Unlock More with ${newTierName}`,
      subheader: `Here's what you get with your new plan`,
      button: "Confirm Upgrade",
      successTitle: "Congratulations!",
      successBody: "You just upgraded your plan. 🥳",
    },
    downgrade: {
      header: `You're Switching to the ${newTierName} Plan`,
      subheader: "Here's what will change:",
      button: "Confirm Downgrade",
      successTitle: "You successfully downgraded your plan.",
      successBody:
        "Your new tier is active and you can re-upgrade any time. 👋",
    },
  });

  const closeModal = () => {
    modal.closeModal({ type: "billingUpgradeDowngradeModal" });
  };

  const { data: activeSubscription } =
    trpc.subscriptions.getActiveSubscriptionByWorkspace.useQuery(
      workspaceId ?? skipToken,
    );

  const { data: nextSubscriptionBillingDate } =
    trpc.billing.getNextBillingDate.useQuery(
      workspaceId && activeSubscription?.id
        ? {
            workspaceId,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const isCurrentPlanMonthly =
    activeSubscription?.billingInterval === "monthly";

  // NOTE (Berkley, 2025-05-08): If the user is upgrading from a monthly plan to a yearly plan,
  // the next billing date is a year from the current date.
  const formattedNextBillingDate = (() => {
    if (type === "upgrade" && isCurrentPlanMonthly && isUpgradePlanYearly) {
      const yearFromNow = new Date();
      yearFromNow.setFullYear(yearFromNow.getFullYear() + 1);
      return yearFromNow.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    return nextSubscriptionBillingDate
      ? new Date(nextSubscriptionBillingDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : null;
  })();

  const handleSubscriptionUpdate = async () => {
    if (!isLoading) {
      const result = await initializeSubscriptionUpdate({
        workspaceId,
        tier: newTier,
        billingInterval: newBillingInterval,
        source,
      });

      if (result.success && !result.confirmationRequired) {
        successToast(
          type === "upgrade" ? "Congratulations!" : "Looks good!",
          type === "upgrade"
            ? "You just upgraded your plan. 🥳"
            : "You downgraded your plan. 👍🏻",
        );
        closeModal();
      }
    }
  };

  const footer = (
    <div className="flex flex-row justify-end gap-2">
      {/* NOTE (Berkley, 2025-05-08): If Upgrade/Downgrade is triggered direct from the billing dashboard manage plans section,
      we don't want to direct back to the billing modal, instead we want to close the modal and do nothing else. */}
      <Button
        variant="secondary"
        size="base"
        onClick={() => {
          closeModal();
          if (source !== "direct.billingDashboardManage") {
            modal.openModal({
              type: "billingModal",
              props: { source },
            });
          }
        }}
        disabled={isLoading}
      >
        Cancel
      </Button>
      <Button
        variant={type === "upgrade" ? "primary" : "danger"}
        size="base"
        isLoading={isLoading}
        onClick={() => void handleSubscriptionUpdate()}
      >
        {copy.button}
      </Button>
    </div>
  );

  const renderDowngradeComparison = () => {
    const currentPlanFeatures = currentPlanInfo.features ?? [];
    const newPlanFeatures = newPlanInfo.features ?? [];

    const uniqueCurrentFeatures = currentPlanFeatures.filter(
      (feature) => !newPlanFeatures.includes(feature),
    );

    const uniqueNewFeatures = newPlanFeatures.filter(
      (feature) => !currentPlanFeatures.includes(feature),
    );

    return (
      <div className="flex flex-col space-y-4">
        <div className="flex flex-row space-x-4">
          <Card className="w-1/2 bg-neutral-soft">
            <CardHeader>
              <CardTitle className="text-left text-xl">
                {currentTierName}
              </CardTitle>
            </CardHeader>
            <CardContent className="gap-1">
              <p className="mb-2 text-left font-semibold">
                You'll lose access to
              </p>
              <div className="space-y-3">
                {uniqueCurrentFeatures.map((feature) => (
                  <div key={feature} className="flex items-start">
                    <span className="text-danger mr-2">✕</span>
                    <span className="text-default line-through">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <div className="flex items-center justify-center">
            <ArrowRight size={24} />
          </div>
          <Card className="w-1/2 bg-danger-soft">
            <CardHeader>
              <CardTitle className="text-left text-xl">{newTierName}</CardTitle>
            </CardHeader>
            <CardContent className="gap-1">
              <p className="mb-2 text-left font-semibold">
                You'll keep access to
              </p>
              <div className="space-y-3">
                {uniqueNewFeatures.map((feature) => (
                  <div key={feature} className="flex items-start">
                    <span className="text-default">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        <Card className="mt-1 bg-neutral-soft">
          <CardContent>
            <div className="flex items-center">
              <div className="mr-2 text-placeholder flex-shrink-0">
                <Info size={20} />
              </div>
              <p className="text-left text-base">
                We'll adjust your plan immediately and issue a prorated refund.
                Your new plan takes effect now.
                {formattedNextBillingDate &&
                  ` Your next billing cycle is ${formattedNextBillingDate}.`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <Modal
      isOpen={true}
      title={copy.header}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      size="lg"
      data-testid="billing-plan-confirmation-modal"
      footer={footer}
    >
      <div className="flex flex-col gap-4 -mt-4">
        {type === "upgrade" ? (
          <div className="min-h-[200px]">
            <div className="mb-4">{copy.subheader}</div>
            <PlanInfoDetails
              plan={newPlanInfo}
              billingInterval={newBillingInterval}
            />
            <Card className="mt-4 bg-neutral-soft">
              <CardContent>
                <div className="flex items-center">
                  <div className="mr-2 text-placeholder flex-shrink-0">
                    <Info size={20} />
                  </div>
                  <p className="text-left text-base">
                    You'll be charged $
                    {isUpgradePlanYearly
                      ? newPlanInfo.yearlyPricePerMonth
                      : newPlanInfo.monthlyPrice}
                    /month (billed{" "}
                    {isUpgradePlanYearly ? "annually" : "monthly"}) starting
                    today.
                    {formattedNextBillingDate
                      ? ` Your next billing cycle is ${formattedNextBillingDate}. `
                      : " "}
                    We'll prorate the rest of this cycle.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="min-h-[400px]">
            <div className="mb-4">{copy.subheader}</div>
            {renderDowngradeComparison()}
          </div>
        )}
      </div>
    </Modal>
  );
};
